import { useEffect, useMemo, useState } from 'react';
import api, { ServicesAPI } from '../api';

export default function AdminServices() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // service object or null
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name:'', shortDescription:'', longDescription:'', netPrice:'', note:'', active:true });
  const [saving, setSaving] = useState(false);
  const [sortKey, setSortKey] = useState(null); // 'name' | 'shortDescription' | 'netPrice' | 'active' | 'createdAt'
  const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'

  async function load() {
    setLoading(true); setError('');
    try {
      const data = await ServicesAPI.list();
      setItems(data);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const sortedItems = useMemo(() => {
    if (!sortKey) return items;
    const list = [...items];
    const getVal = (it) => {
      switch (sortKey) {
        case 'name': return (it.name || '').toLowerCase();
        case 'shortDescription': return (it.shortDescription || '').toLowerCase();
        case 'netPrice': return Number(it.netPrice ?? 0);
        case 'active': return it.active ? 1 : 0;
        case 'createdAt': return new Date(it.createdAt).getTime() || 0;
        default: return 0;
      }
    };
    list.sort((a, b) => {
      const va = getVal(a);
      const vb = getVal(b);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [items, sortKey, sortDir]);

  function toggleSort(key, defaultDir = 'asc') {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(defaultDir);
    }
  }

  function startCreate() {
    setEditing(null);
    setCreating(true);
    setForm({ name:'', shortDescription:'', longDescription:'', netPrice:'', note:'', active:true });
  }
  function startEdit(it) {
    setCreating(false);
    setEditing(it);
    setForm({
      name: it.name || '',
      shortDescription: it.shortDescription || '',
      longDescription: it.longDescription || '',
      netPrice: it.netPrice ?? '',
      note: it.note || '',
      active: !!it.active,
    });
  }
  function cancelForm() {
    setCreating(false);
    setEditing(null);
    setForm({ name:'', shortDescription:'', longDescription:'', netPrice:'', note:'', active:true });
  }
  function normalizePriceInput(v) {
    if (v === undefined || v === null || v === '') return '';
    const s = String(v).trim().replace(',', '.');
    return s;
  }
  function formatPriceDisplay(v) {
    const n = Number(v);
    if (!isFinite(n)) return '';
    return n.toFixed(2).replace('.', ',') + ' €';
  }
  async function save() {
    setSaving(true); setError('');
    try {
      // client-side validation
      const name = String(form.name || '').trim();
      if (!name) { setError('Name erforderlich'); return; }
      // normalize price
      let netPrice = form.netPrice;
      if (netPrice !== '' && netPrice !== null && netPrice !== undefined) {
        const normalized = normalizePriceInput(netPrice);
        const n = Number(normalized);
        if (!isFinite(n) || n < 0) { setError('Ungültiger Preis'); return; }
        netPrice = n.toFixed(2);
      }
      const payload = { ...form, name };
      if (netPrice === '' || netPrice === null || netPrice === undefined) delete payload.netPrice;
      else payload.netPrice = netPrice;
      if (editing) await ServicesAPI.update(editing.id, payload);
      else await ServicesAPI.create(payload);
      cancelForm();
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally { setSaving(false); }
  }
  async function remove(it) {
    if (!confirm(`Dienstleistung "${it.name}" wirklich löschen?`)) return;
    try { await ServicesAPI.remove(it.id); await load(); }
    catch (e) { setError(e?.response?.data?.error || e.message); }
  }

  return (
    <div className="home-page">
      {/* Outer full-width header section */}
      <section style={{ width:'100vw', margin:0, padding:0, position:'relative', left:'-120px' }}>
        <h2 className="hero-title" style={{ textAlign: 'center', margin:'0 0 8px' }}>Dienstleistungen</h2>
        <p className="hero-desc" style={{ margin:'0 auto 24px', textAlign:'center', maxWidth:760 }}>Verwalte deine angebotenen Services.</p>
      </section>
      {/* Centered content container */}
      <section style={{ width: '100%', maxWidth: 900, margin: '0 auto', padding: '0 16px 40px 16px', color: '#fff' }}>
        {error && <div style={{ color: 'tomato', marginBottom: 8 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <button className="hero-btn-primary" onClick={startCreate} disabled={creating || !!editing}>Neu anlegen</button>
        </div>

        {loading ? (
          <div>Lade ...</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th onClick={() => toggleSort('name')} style={{ cursor:'pointer', userSelect:'none', textAlign: 'left', borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                  Name {sortKey === 'name' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                </th>
                <th onClick={() => toggleSort('shortDescription')} style={{ cursor:'pointer', userSelect:'none', textAlign: 'left', borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                  Kurzbeschreibung {sortKey === 'shortDescription' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                </th>
                <th onClick={() => toggleSort('netPrice')} style={{ cursor:'pointer', userSelect:'none', textAlign: 'left', borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                  Preis (netto) {sortKey === 'netPrice' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                </th>
                <th onClick={() => toggleSort('active')} style={{ cursor:'pointer', userSelect:'none', textAlign: 'left', borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                  Aktiv {sortKey === 'active' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                </th>
                <th onClick={() => toggleSort('createdAt', 'desc')} style={{ cursor:'pointer', userSelect:'none', textAlign: 'left', borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                  Aktionen {sortKey === 'createdAt' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map(it => (
                <tr key={it.id}>
                  <td style={{ padding: '6px 8px' }}>{it.name}</td>
                  <td style={{ padding: '6px 8px', opacity: .9 }}>{it.shortDescription}</td>
                  <td style={{ padding: '6px 8px' }}>{formatPriceDisplay(it.netPrice)}</td>
                  <td style={{ padding: '6px 8px' }}>{it.active ? 'Ja' : 'Nein'}</td>
                  <td style={{ padding: '6px 8px' }}>
                    <button className="hero-btn-secondary" onClick={() => startEdit(it)}>Bearbeiten</button>
                    <button className="hero-btn-secondary" style={{ marginLeft: 8 }} onClick={() => remove(it)}>Löschen</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Bottom create button below the list */}
        {!loading && !creating && editing === null && (
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="hero-btn-primary" onClick={startCreate}>Neu anlegen</button>
          </div>
        )}

        {(creating || editing !== null) && (
          <div style={{ marginTop: 24, borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: 16 }}>
            <h3 style={{ margin: 0, marginBottom: 8 }}>{editing ? 'Dienstleistung bearbeiten' : 'Neue Dienstleistung anlegen'}</h3>
            <div style={{ display: 'grid', gap: 8 }}>
              <div>
                <div>Name</div>
                <input className="hero-btn-secondary" type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div>
                <div>Kurzbeschreibung</div>
                <input className="hero-btn-secondary" type="text" value={form.shortDescription} onChange={e => setForm(f => ({ ...f, shortDescription: e.target.value }))} />
              </div>
              <div>
                <div>Langbeschreibung</div>
                <textarea className="hero-btn-secondary" rows={5} value={form.longDescription} onChange={e => setForm(f => ({ ...f, longDescription: e.target.value }))} />
              </div>
              <div>
                <div>Netto-Preis (Einzelbuchung)</div>
                <input className="hero-btn-secondary" type="text" inputMode="decimal" placeholder="z.B. 99,00" value={form.netPrice} onChange={e => setForm(f => ({ ...f, netPrice: e.target.value }))} />
              </div>
              <div>
                <div>Hinweis</div>
                <textarea className="hero-btn-secondary" rows={3} value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} /> Aktiv
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="hero-btn-primary" disabled={saving} onClick={save}>{saving ? 'Speichere...' : 'Speichern'}</button>
                <button className="hero-btn-secondary" onClick={cancelForm}>Abbrechen</button>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
